// Clerk is loaded from its CDN (clerk.browser.js), and its config (publishable
// key + org slug) is fetched at runtime from /api/public-config — so a single
// build runs unchanged across dev/prod, configured by the server's env/secrets.

let publishableKey = '';
let orgSlug = '';
let clerkInstance = null;
let loadPromise = null;

// The Frontend API host is base64-encoded inside the publishable key:
//   pk_(test|live)_<base64("<host>$")>
function frontendApiHost(pk) {
  const b64 = pk.replace(/^pk_(test|live)_/, '');
  try {
    return atob(b64).replace(/\$+$/, '');
  } catch (e) {
    return '';
  }
}

function loadScript(src, attrs = {}) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.crossOrigin = 'anonymous';
    for (const [k, v] of Object.entries(attrs)) s.setAttribute(k, v);
    s.src = src;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Failed to load ' + src));
    document.head.appendChild(s);
  });
}

// Resolves to the Clerk instance, or null if the server has no publishable key
// configured. Idempotent.
export function initClerk() {
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    const cfg = await fetch('/api/public-config').then((r) => r.json()).catch(() => ({}));
    publishableKey = cfg.clerkPublishableKey || '';
    orgSlug = cfg.orgSlug || '';
    if (!publishableKey) return null;

    const host = frontendApiHost(publishableKey);
    if (!host) throw new Error('Could not derive Clerk Frontend API from publishable key');

    if (!window.Clerk) {
      // Core 2 (v6) splits the prebuilt UI into a separate @clerk/ui bundle that must load
      // BEFORE clerk.browser.js and be passed into load() — else mountSignIn throws
      // "Clerk was not loaded with UI components". Pin majors explicitly (never @latest for
      // an auth SDK; the FAPI's @latest alias served the deprecated Core 1 line, v4.73.14).
      await loadScript(`https://${host}/npm/@clerk/ui@1/dist/ui.browser.js`);
      await loadScript(`https://${host}/npm/@clerk/clerk-js@6/dist/clerk.browser.js`, {
        'data-clerk-publishable-key': publishableKey,
      });
    }

    clerkInstance = window.Clerk;
    await clerkInstance.load({ ui: { ClerkUI: window.__internal_ClerkUICtor } });

    // Auto-activate the staff member's organization so the session token carries
    // org claims (the API requires an active org membership).
    if (clerkInstance.user && !clerkInstance.organization) {
      const memberships = clerkInstance.user.organizationMemberships || [];
      const target = orgSlug
        ? memberships.find((m) => m.organization.slug === orgSlug)
        : memberships[0];
      if (target) {
        try { await clerkInstance.setActive({ organization: target.organization.id }); } catch (e) { /* ignore */ }
      }
    }
    return clerkInstance;
  })();

  return loadPromise;
}

export function getClerk() {
  return clerkInstance;
}

// True when the signed-in user has an active organization matching the
// configured slug (or any org, if no slug is pinned).
export function hasOrgAccess() {
  const org = clerkInstance && clerkInstance.organization;
  if (!org) return false;
  return orgSlug ? org.slug === orgSlug : true;
}

// True when the user is an ADMIN of the active org ('org:admin' or legacy
// 'admin'). Plain members only get the Annotate tab; the server enforces the
// same split on /api/orders and /api/ecards.
export function isOrgAdmin() {
  const org = clerkInstance && clerkInstance.organization;
  const user = clerkInstance && clerkInstance.user;
  if (!org || !user) return false;
  const membership = (user.organizationMemberships || [])
    .find((m) => m.organization.id === org.id);
  return /(^|:)admin$/.test((membership && membership.role) || '');
}
